import { _dataProvider } from '../dataProvider/DataProvider';
import type { IGroupItemView } from './types';

export function groupDisplayName(group: IGroupItemView): string {
  const tag = group.isOneOnOneGroup && group.friendUserId ? _dataProvider.getMyTagForUser(group.friendUserId) : '';
  return `${group.name || ''} ${tag || ''}`.trim();
}

export function getPendingReadStorageKey(userId: number | string): string {
  return `web.pendingReadGroups/${userId}`;
}

export function loadPendingReadGroupIds(storageKey: string | null): Set<number> {
  if (!storageKey) {
    return new Set();
  }
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return new Set();
    }
    const parsed = JSON.parse(raw);
    const ids = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.groupIds) ? parsed.groupIds : [];
    return new Set(ids.map((id: unknown) => Number(id)).filter((id: number) => Number.isFinite(id)));
  } catch {
    return new Set();
  }
}

export function savePendingReadGroupIds(storageKey: string | null, ids: Set<number>): void {
  if (!storageKey) {
    return;
  }
  try {
    localStorage.setItem(storageKey, JSON.stringify({ groupIds: Array.from(ids) }));
  } catch {
    // Ignore localStorage failures
  }
}

export function normalizeTopGroupIds(ids: unknown): number[] {
  if (!Array.isArray(ids)) {
    return [];
  }
  const seen = new Set<number>();
  const result: number[] = [];
  for (const id of ids) {
    const n = Number(id);
    if (!Number.isFinite(n) || seen.has(n)) {
      continue;
    }
    seen.add(n);
    result.push(n);
  }
  return result;
}

export function isTopGroupId(topGroupIds: ReadonlySet<number> | readonly number[], groupId: number): boolean {
  const id = Number(groupId);
  if (!Number.isFinite(id)) {
    return false;
  }
  if (topGroupIds instanceof Set) {
    return topGroupIds.has(id);
  }
  return (topGroupIds as readonly number[]).some((topId) => Number(topId) === id);
}

/** Keep pinned groups first; prioritize unread regular groups without changing the incoming recency order among peers. */
export function sortGroupsWithTopFirst<T extends { groupId: number }>(
  groups: T[],
  topGroupIds: ReadonlySet<number> | readonly number[],
  unreadGroupIds?: ReadonlySet<number>
): T[] {
  return [...groups].sort((a, b) => {
    const aTop = isTopGroupId(topGroupIds, a.groupId) ? 1 : 0;
    const bTop = isTopGroupId(topGroupIds, b.groupId) ? 1 : 0;
    if (aTop !== bTop) {
      return bTop - aTop;
    }
    if (aTop) {
      return 0;
    }
    const aUnread = unreadGroupIds?.has(a.groupId) ? 1 : 0;
    const bUnread = unreadGroupIds?.has(b.groupId) ? 1 : 0;
    return bUnread - aUnread;
  });
}

export function toggleTopGroupIds(topGroupIds: readonly number[], groupId: number, isCurrentlyTop: boolean): number[] {
  const id = Number(groupId);
  if (!Number.isFinite(id)) {
    return normalizeTopGroupIds(topGroupIds);
  }
  const current = normalizeTopGroupIds(topGroupIds);
  if (isCurrentlyTop) {
    return current.filter((topId) => topId !== id);
  }
  if (current.includes(id)) {
    return current;
  }
  return [...current, id];
}
